import {useEffect} from 'react';
import {MsalProvider, useMsal} from '@azure/msal-react';
import {EventType} from '@azure/msal-browser';
import {Route, Routes} from 'react-router-dom';
import {b2cPolicies} from './authConfig';
import {Layout} from "./components/universal/Layout";
import {Home} from "./components/Home";
import UniversalCalendar from "./components/installation/UniversalCalendar";
import EnquiryNewList from "./components/enquiries/EnquiryNewList";

import EnquiryView from "./components/enquiries/EnquiryView";

import {ManagementHub} from "./components/management/ManagementHub";

import {JobView} from "./components/management/JobView";
import {AdminOverview} from "./components/management/AdminOverview";
import {DesignOverview} from "./components/management/DesignOverview";
import {PrintOverview} from "./components/management/PrintingOverview";
import {EmployeeOverview} from "./components/management/EmployeeOverview";
import {EmployeeView} from "./components/management/EmployeeView";
import AddEmployeeForm from "./components/management/AddEmployeeForm";
import PrintNewList from "./components/management/PrintNewList";
import {PrintFullList} from "./components/management/PrintFullList";
import {PrintCompletedList} from "./components/management/PrintCompleteList";
import {JobEventView} from "./components/management/JobEventView";
import {PrintView} from "./components/management/PrintView";
import {DesignNewList} from "./components/management/DesignNewList";
import {DesignCompleteList} from "./components/management/DesignCompleteList";
import {DesignFullList} from "./components/management/DesignFullList";
import DesignView from "./components/management/DesignView";
import {VehicleEventView} from "./components/management/VehicleEventView";


import InstallationHub from "./components/installation/Hub";
import InstallationUnassigned from "./components/installation/Unassigned";
import InstallationAssigned from "./components/installation/Assigned";
import InstallationCompleted from "./components/installation/Completed";
import InstallationAssignJob from "./components/installation/AssignJob";
import InstallationJob from "./components/installation/Job";
import InstallationVehicle from "./components/installation/Vehicle";
import EnquiryApprovedList from "./components/enquiries/EnquiryApprovedList";
import {EnquiryFullList} from "./components/enquiries/EnquiryFullList";
import {JobFullList} from "./components/management/JobFullList";
import {JobNewList} from "./components/management/JobNewList";
import {JobCompleteList} from "./components/management/JobCompleteList";
import {JobOverview} from "./components/management/JobOverview";
import {HttpClientProvider} from "./services/http-client-new";
import EnquiryForm from "./components/enquiries/EnquiryForm";

const Pages = () => {
    /**
     * useMsal is hook that returns the PublicClientApplication instance,
     * an array of all accounts currently signed in and an inProgress value
     * that tells you what msal is currently doing. For more, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
     */
    const {instance} = useMsal();
    useEffect(() => {
        const callbackId = instance.addEventCallback((event) => {
            if (
                (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
                event.payload.account
            ) {
                /**
                 * For the purpose of setting an active account for UI update, we want to consider only the auth
                 * response resulting from SUSI flow. "tfp" claim in the id token tells us the policy (NOTE: legacy
                 * policies may use "acr" instead of "tfp"). To learn more about B2C tokens, visit:
                 * https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
                 */
                if (event.payload.idTokenClaims['tfp'] === b2cPolicies.names.editProfile) {
                    // retrieve the account from initial sing-in to the app
                    const originalSignInAccount = instance
                        .getAllAccounts()
                        .find(
                            (account) =>
                                account.idTokenClaims.oid === event.payload.idTokenClaims.oid &&
                                account.idTokenClaims.sub === event.payload.idTokenClaims.sub &&
                                account.idTokenClaims['tfp'] === b2cPolicies.names.signUpSignIn
                        );

                    let signUpSignInFlowRequest = {
                        authority: b2cPolicies.authorities.signUpSignIn.authority,
                        account: originalSignInAccount,
                    };

                    // silently login again with the signUpSignIn policy
                    instance.ssoSilent(signUpSignInFlowRequest);
                }

                /**
                 * Below we are checking if the user is returning from the reset password flow.
                 * If so, we will ask the user to reauthenticate with their new password.
                 * If you do not want this behavior and prefer your users to stay signed in instead,
                 * you can replace the code below with the same pattern used for handling the return from
                 * profile edit flow
                 */
                if (event.payload.idTokenClaims['tfp'] === b2cPolicies.names.forgotPassword) {
                    let signUpSignInFlowRequest = {
                        authority: b2cPolicies.authorities.signUpSignIn.authority,
                        scopes: [],
                    };
                    instance.loginRedirect(signUpSignInFlowRequest);
                }
            }

            if (event.eventType === EventType.LOGIN_FAILURE) {
                // Check for forgot password error
                // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
                if (event.error && event.error.errorMessage.includes('AADB2C90118')) {
                    const resetPasswordRequest = {
                        authority: b2cPolicies.authorities.forgotPassword.authority,
                        scopes: [],
                    };
                    instance.loginRedirect(resetPasswordRequest);
                }
            }
        });

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, [instance]);

    return (
        <Routes>

            <Route path="/" element={<Home/>}/>

            {/* enquiries */}
            <Route path="/enquiries" element={<AdminOverview/>}/>
            <Route path="/enquiries/unapproved" element={<EnquiryNewList/>}/>
            <Route path="/enquiries/approved" element={<EnquiryApprovedList/>}/>

            {/* design */}
            <Route path="/design" element={<DesignOverview/>}/>
            <Route path="/design/current" element={<DesignNewList/>}/>
            <Route path="/design/completed" element={<DesignCompleteList/>}/>
            <Route path="/design/view" element={<DesignView/>}/>

            {/* production */}
            <Route path="/production" element={<PrintOverview/>}/>
            <Route path="/production/current" element={<PrintNewList/>}/>
            <Route path="/production/completed" element={<PrintCompletedList/>}/>

            {/* installation */}
            <Route path="/installation" element={<InstallationHub/>}/>
            <Route path="/installation/unassigned" element={<InstallationUnassigned/>}/>
            <Route path="/installation/assigned" element={<InstallationAssigned/>}/>
            <Route path="/installation/completed" element={<InstallationCompleted/>}/>
            <Route path="/installation/calendar" element={< UniversalCalendar/>}/>
            <Route path="/installation/assign" element={<InstallationAssignJob/>}/>
            <Route path="/installation/view" element={<InstallationJob/>}/>
            <Route path="/installation/view/details" element={<InstallationVehicle/>}/>

            <Route path="/management/hub" element={<ManagementHub/>}/>
            <Route path="/management/jobs" element={<JobOverview/>}/>
            <Route path="/management/jobs/view-all-jobs" element={<JobFullList/>}/>
            <Route path="/management/jobs/view-new-jobs" element={<JobNewList/>}/>
            <Route path="/management/jobs/view-completed-jobs" element={<JobCompleteList/>}/>
            <Route path="/management/jobs/view" element={<JobView/>}/>
            <Route path="/management/jobs/view/timeline" element={<JobEventView/>}/>
            <Route path="/management/jobs/view/vehicle-timeline" element={<VehicleEventView/>}/>

            <Route path="/management/admin" element={<AdminOverview/>}/>
            <Route path="/management/admin/view-new-enquiries" element={<EnquiryNewList/>}/>
            <Route path="management/admin/view-approved-enquiries" element={<EnquiryApprovedList/>}/>
            <Route path="management/admin/view-all-enquiries" element={<EnquiryFullList/>}/>
            <Route path="management/admin/enquiry-form" element={<EnquiryForm/>}/>
            <Route path="management/admin/view" element={<EnquiryView/>}/>

            <Route path="/management/design" element={<DesignOverview/>}/>
            <Route path="/management/design/new-jobs" element={<DesignNewList/>}/>
            <Route path="/management/design/completed-jobs" element={<DesignCompleteList/>}/>
            <Route path="/management/design/all-jobs" element={<DesignFullList/>}/>
            <Route path="/management/design/view" element={<DesignView/>}/>

            <Route path="/management/print" element={<PrintOverview/>}/>
            <Route path="/management/print/new-jobs" element={<PrintNewList/>}/>
            <Route path="/management/print/completed-jobs" element={<PrintCompletedList/>}/>
            <Route path="/management/print/all-jobs" element={<PrintFullList/>}/>
            <Route path="/management/print/view" element={<PrintView/>}/>

            <Route path="/management/employees" element={<EmployeeOverview/>}/>
            <Route path="/management/employees/view" element={<EmployeeView/>}/>
            <Route path="/management/employees/add-employee" element={<AddEmployeeForm/>}/>

            {/* Not used in this project anymore*/}
            <Route path="/admin/view-new-enquiries" element={<EnquiryNewList/>}/>
            <Route path="/admin/view-approved-enquiries" element={<EnquiryNewList/>}/>
            <Route path="/admin/view-all-enquiries" element={<EnquiryFullList/>}/>

            <Route path="/installation" element={<InstallationHub/>}/>
            <Route path="/installation/unassigned" element={<InstallationUnassigned/>}/>
            <Route path="/installation/assigned" element={<InstallationAssigned/>}/>
            <Route path="/installation/completed" element={<InstallationCompleted/>}/>
            <Route path="/installation/assign" element={<InstallationAssignJob/>}/>
            <Route path="/installation/view" element={<InstallationJob/>}/>
            <Route path="/installation/view/details" element={<InstallationVehicle/>}/>
        </Routes>
    );
};

const App = ({instance}) => {
    return (
        <MsalProvider instance={instance}>
            <HttpClientProvider>
                <Layout>
                    <Pages/>
                </Layout>
            </HttpClientProvider>
        </MsalProvider>
    );
};

export default App;